import React, { useState, forwardRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DataView } from 'primereact/dataview';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { OldCostituenteSite, REACT_APP_INTERVENTI } from '../../config';


const AVATAR_SIZE = 48;
const AVATAR_PLACEHOLDER_BG = "#e5e7eb";
const SEARCH_TOKEN_REGEX = /[a-z0-9]+/g;

const buildDeputatoId = (id) => {
    if (!id) return null;
    const n = parseInt(id.slice(2), 10);
    return Number.isNaN(n) ? null : `d${n}`;
};

const buildDeputatoNumericId = (id) => {
    if (!id) return null;
    const n = parseInt(id.slice(2), 10);
    return Number.isNaN(n) ? null : `${n}`;
};

const buildFotoUrl = (depId) =>
    `https://documenti.camera.it/_dati/costituente/costituenti/foto/${depId}.jpg`;

const buildProfiloUrl = (id) => {
    const numericId = buildDeputatoNumericId(id);

    if (!numericId) return null;

    if (OldCostituenteSite) {
        return `https://legislature.camera.it/chiosco.asp?cp=1&content=altre_sezioni/assemblea_costituente/composizione/costituenti/framedeputato.asp?Deputato=d${numericId}`;
    }

    return `https://costituente.camera.it/deputato/${numericId}`;
};

const SEDE_MAP = {
    "Assemblea": "assemblea",
    "Commissione per la Costituzione": "commissione",
    "I Sottocommissione": "I_sottocommissione",
    "II Sottocommissione": "II_sottocommissione",
    "II Sottocommissione I Sezione": "II_sottocommissione_I_sezione",
    "II Sottocommissione II Sezione": "II_sottocommissione_II_sezione",
    "III Sottocommissione": "III_sottocommissione",
};

const buildPdfUrl = (sede, numeroSeduta, isAll = false) => {
    const sedeSlug = SEDE_MAP[sede];
    if (!sedeSlug) return null;
    const n = String(numeroSeduta).padStart(3, "0");
    const suffix = isAll ? "_all" : "nc";
    return `https://documenti.camera.it/_dati/costituente/lavori/${sedeSlug}/sed${n}/sed${n}${suffix}.pdf`;
};

const normalizeSearchValue = (value) =>
    String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

const escapeRegex = (value) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildWholeWordMatcher = (value) => {
    const normalizedValue = normalizeSearchValue(value);

    if (!normalizedValue || !(normalizedValue.match(SEARCH_TOKEN_REGEX)?.length)) {
        return null;
    }

    const escapedValue = escapeRegex(normalizedValue).replace(/\s+/g, "\\s+");
    return new RegExp(`(^|[^a-z0-9])(${escapedValue})(?=$|[^a-z0-9])`, "g");
};

const matchesWholeWordFilter = (value, matcher) => {
    if (!matcher) {
        return true;
    }

    matcher.lastIndex = 0;
    return matcher.test(normalizeSearchValue(value));
};

const buildNormalizedTextMap = (value) => {
    const originalText = String(value ?? "");
    let normalizedText = "";
    const indexMap = [];

    for (let i = 0; i < originalText.length; i += 1) {
        const normalizedChunk = originalText[i]
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

        for (const char of normalizedChunk) {
            normalizedText += char;
            indexMap.push(i);
        }
    }

    return { originalText, normalizedText, indexMap };
};

const highlightMatchingWords = (value, matcher) => {
    if (!matcher) {
        return value;
    }

    const { originalText, normalizedText, indexMap } = buildNormalizedTextMap(value);

    if (!originalText) {
        return originalText;
    }

    const ranges = [];
    let match;
    matcher.lastIndex = 0;

    while ((match = matcher.exec(normalizedText)) !== null) {
        const leadingBoundary = match[1] ?? "";
        const matchedValue = match[2] ?? "";
        const startIndex = match.index + leadingBoundary.length;
        const endIndex = startIndex + matchedValue.length;
        const originalStart = indexMap[startIndex];
        const originalEnd = indexMap[endIndex - 1] + 1;

        if (originalStart != null && originalEnd != null) {
            ranges.push({ start: originalStart, end: originalEnd });
        }

        if (matcher.lastIndex === match.index) {
            matcher.lastIndex += 1;
        }
    }

    matcher.lastIndex = 0;

    if (ranges.length === 0) {
        return originalText;
    }

    const highlightedText = [];
    let cursor = 0;

    ranges.forEach(({ start, end }, index) => {
        if (cursor < start) {
            highlightedText.push(
                <React.Fragment key={`text-${index}-${cursor}`}>
                    {originalText.slice(cursor, start)}
                </React.Fragment>
            );
        }

        highlightedText.push(
            <mark key={`mark-${index}-${start}`} className="result-filter-highlight">
                {originalText.slice(start, end)}
            </mark>
        );
        cursor = end;
    });

    if (cursor < originalText.length) {
        highlightedText.push(
            <React.Fragment key={`text-final-${cursor}`}>
                {originalText.slice(cursor)}
            </React.Fragment>
        );
    }

    return highlightedText;
};

/**
 * Raggruppa gli interventi per persona, ordina alfabeticamente per cognome/nome,
 * e per ciascuna persona raccoglie le pagine (deduplicate e ordinate).
 */
const groupInterventiByPersona = (interventi) => {
    const map = new Map();

    for (const intervento of interventi) {
        const key = intervento.id;

        if (!map.has(key)) {
            map.set(key, {
                id: intervento.id,
                cognome: intervento.cognome,
                nome: intervento.nome,
                ruolo: intervento.ruolo,
                carica: intervento.carica,
                governo: intervento.governo,
                pagine: [],
            });
        }

        const pagina = Number(intervento.pagina);
        if (!Number.isNaN(pagina)) {
            const paginapdf = intervento.paginapdf != null ? Number(intervento.paginapdf) : null;
            map.get(key).pagine.push({ pagina, paginapdf });
        }
    }

    const grouped = Array.from(map.values());

    // ordina alfabeticamente per cognome, poi nome
    grouped.sort((a, b) => {
        const cmp = (a.cognome ?? '').localeCompare(b.cognome ?? '', 'it');
        return cmp !== 0 ? cmp : (a.nome ?? '').localeCompare(b.nome ?? '', 'it');
    });

    // deduplica (per pagina) e ordina le pagine per ciascuna persona
    for (const g of grouped) {
        const seen = new Map();
        for (const entry of g.pagine) {
            if (!seen.has(entry.pagina)) seen.set(entry.pagina, entry);
        }
        g.pagine = [...seen.values()].sort((a, b) => a.pagina - b.pagina);
    }

    return grouped;
};

const RisultatiRicerca = forwardRef(function RisultatiRicerca({ results = [], searched }, ref) {
    const [interventiState, setInterventiState] = useState({});
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [resultFilter, setResultFilter] = useState("");

    const wholeWordMatcher = useMemo(() => buildWholeWordMatcher(resultFilter), [resultFilter]);

    useEffect(() => {
        setResultFilter('');
        setFirst(0);
    }, [results]);

    useEffect(() => {
        setFirst(0);
    }, [resultFilter]);

    const filteredResults = useMemo(() => {
        if (!wholeWordMatcher) {
            return results;
        }

        return results.filter((item) =>
            [item.argomento, item.denominazione].some((value) =>
                matchesWholeWordFilter(value, wholeWordMatcher)
            )
        );
    }, [results, wholeWordMatcher]);

    const hasActiveFilter = resultFilter.trim().length > 0;

    if (!searched) return null;

    const getItemId = (item) => `${item.numeroSeduta}-${item.dataSeduta}-${item.sede}-${item.argomento}-${item.denominazione}`;

    const toggleInterventi = async (item) => {
        const id = getItemId(item);
        const current = interventiState[id];

        if (current?.data) {
            setInterventiState((prev) => ({
                ...prev,
                [id]: { ...current, expanded: !current.expanded },
            }));
            return;
        }

        setInterventiState((prev) => ({
            ...prev,
            [id]: { loading: true, expanded: true },
        }));

        try {
            const body = {
                ricerca: item.ricerca,
                tipoSeduta: item.tipoSeduta ?? null,
                numeroSeduta: String(item.numeroSeduta).padStart(3, "0"),
                sede: item.sede,
                argomento: item.argomento,
                dataSeduta: item.dataSeduta,
                denominazione: item.denominazione,
            };

            const { data } = await axios.post(REACT_APP_INTERVENTI, body);

            data.forEach((intervento) => {
                const depId = buildDeputatoId(intervento.id);
                if (depId) {
                    const img = new Image();
                    img.src = buildFotoUrl(depId);
                }
            });

            setInterventiState((prev) => ({
                ...prev,
                [id]: { loading: false, expanded: true, data },
            }));
        } catch (err) {
            setInterventiState((prev) => ({
                ...prev,
                [id]: {
                    loading: false,
                    expanded: true,
                    error: 'Errore nel caricamento degli interventi',
                },
            }));
        }
    };

    const renderInterventi = (state, pdfUrl) => {
        if (state.error) {
            return <div className="text-red-500">Impossibile ricercare gli interventi</div>;
        }
        if (!state.data || state.data.length === 0) {
            return <div className="text-600">Nessun intervento disponibile</div>;
        }

        const grouped = groupInterventiByPersona(state.data);

        return (
            <ul className="list-none p-0 m-0">
                {grouped.map((persona) => {
                    const depId = buildDeputatoId(persona.id);
                    const fotoUrl = depId && buildFotoUrl(depId);
                    const profiloUrl = buildProfiloUrl(persona.id);

                    const nomeCompleto = `${persona.cognome} ${persona.nome}`;

                    const descrizione = persona.ruolo ||
                        (persona.carica || persona.governo
                            ? `${persona.carica ?? ''} ${persona.governo ?? ''}`.trim()
                            : null);

                    return (
                        <li
                            key={persona.id ?? `${persona.cognome}-${persona.nome}`}
                            className="flex align-items-center pb-2 gap-2"
                        >
                            <div
                                style={{
                                    width: AVATAR_SIZE,
                                    height: AVATAR_SIZE,
                                    borderRadius: "50%",
                                    backgroundColor: AVATAR_PLACEHOLDER_BG,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                }}
                            >
                                {fotoUrl && (
                                    <img
                                        src={fotoUrl}
                                        alt={nomeCompleto}
                                        decoding="async"
                                        width={AVATAR_SIZE}
                                        height={AVATAR_SIZE}
                                        style={{
                                            display: "block",
                                            transform: "scale(1.1)",
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                                    />
                                )}
                            </div>
                            <div className="flex-1">
                                <span className="font-semibold">
                                    {profiloUrl ? (
                                        <a
                                            href={profiloUrl}
                                            target="_blank"
                                            title="Vai alla scheda personale"
                                            rel="noopener noreferrer"
                                        >
                                            {nomeCompleto}
                                        </a>
                                    ) : (
                                        nomeCompleto
                                    )}
                                </span>
                                {descrizione && (
                                    <span>, <em>{descrizione}</em></span>
                                )}
                                {persona.pagine.length > 0 && (
                                    <>
                                        <span>, {persona.pagine.length === 1 ? 'pag.' : 'pagg.'} </span>
                                        {persona.pagine.map(({ pagina, paginapdf }, i) => (
                                            <React.Fragment key={pagina}>
                                                {i > 0 && ', '}
                                                {pdfUrl && paginapdf != null ? (
                                                    <a
                                                        href={`${pdfUrl}#page=${paginapdf}&zoom=95,0,70`}
                                                        target="_blank"
                                                        title="Vai all'intervento nel resoconto in formato PDF"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {pagina}
                                                    </a>
                                                ) : (
                                                    <span>{pagina}</span>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const itemTemplate = (item) => {
        const id = getItemId(item);
        const state = interventiState[id] || {};
        const argomento = item.argomento ?? '';
        const denominazione = item.denominazione ?? '';
        const sameHeading = argomento.trim() === denominazione.trim();

        const isAll = item.ricerca === "NL" &&
            item.denominazione === "INTERROGAZIONI CON RISPOSTA SCRITTA (risposta pervenuta)";
        const pdfUrl = buildPdfUrl(item.sede, item.numeroSeduta, isAll);

        return (
            <Card
                pt={{
                    root: { className: 'border-1 cardresult shadow-none border-round-lg w-full mb-2' },
                    body: { className: 'py-2 px-3' },
                    content: { className: 'p-0' },
                }}
            >
                <div className="flex align-items-center">
                    <div className="flex-1 font-bold" style={{ fontSize: "1.1rem" }}>
                        Seduta n. {Number(item.numeroSeduta)} del {item.dataSeduta.slice(0, 10).split('-').reverse().join('/')} - {item.sede}
                        {item.tipoSeduta && (
                            <span style={{ fontSize: "0.90rem", color: "#838282", fontWeight: "normal", marginLeft: "0.5rem" }}>
                                {item.tipoSeduta === "ant." ? "(seduta antimeridiana)" : "(seduta pomeridiana)"}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-column md:flex-row gap-2">
                        <Button
                            label={state.expanded ? 'Nascondi interventi' : 'Mostra interventi'}
                            size="small"
                            outlined
                            loading={state.loading}
                            onClick={() => toggleInterventi(item)}
                            pt={{
                                root: { className: 'pt-2 pb-2 pl-2 pr-2' },
                            }}
                        />
                        {pdfUrl ? (
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title='Vai al resoconto in formato PDF'
                            >
                                <Button
                                    label="PDF"
                                    size="small"
                                    outlined
                                    severity="danger"
                                    pt={{
                                        root: { className: 'pt-2 pb-2 pl-2 pr-2 w-full' },
                                    }}
                                />
                            </a>
                        ) : (
                            <Button
                                label="PDF"
                                size="small"
                                outlined
                                severity="danger"
                                disabled
                                pt={{
                                    root: { className: 'pt-2 pb-2 pl-2 pr-2' },
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className='mt-1'>
                    {sameHeading ? (
                        highlightMatchingWords(argomento, wholeWordMatcher)
                    ) : (
                        <>
                            {highlightMatchingWords(argomento, wholeWordMatcher)}
                            {' - '}
                            {highlightMatchingWords(denominazione, wholeWordMatcher)}
                        </>
                    )}
                </div>
                {state.expanded && !state.loading && (
                    <div className="mt-2 pt-3">
                        {renderInterventi(state, pdfUrl)}
                    </div>
                )}
            </Card>
        );
    };

    return (
        <div ref={ref} className='pb-3'>
            <Divider></Divider>
            <div className="grid ml-4 mr-4 mb-3 align-items-center">
                <div className="col-12 lg:col-9">
                    <div className="font-semibold text-xl">
                        Elenco dei dibattiti che rispondono ai parametri di ricerca inseriti: {filteredResults.length} dibattit{filteredResults.length === 1 ? 'o' : 'i'}{hasActiveFilter ? ` su ${results.length}` : ''}.
                    </div>
                </div>
                <div className="col-12 lg:col-3">
                    <div className="flex flex-column lg:align-items-end">
                       
                        <div className="flex flex-row sm:flex-row gap-2 w-full">
                            <IconField iconPosition="left" className="flex-1">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    id="filtro-risultati"
                                    value={resultFilter}
                                    onChange={(e) => setResultFilter(e.target.value)}
                                    placeholder="Filtra argomento"
                                    className="w-full"
                                />
                            </IconField>
                            {hasActiveFilter && (
                                <Button
                                    type="button"
                                    label="Pulisci"
                                    size='small'
                                    outlined
                                    onClick={() => setResultFilter('')}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <DataView
                value={filteredResults}
                itemTemplate={itemTemplate}
                paginator
                rows={rows}
                rowsPerPageOptions={[10, 15, 20]}
                layout="list"
                emptyMessage={hasActiveFilter ? "Nessun dibattito corrisponde al filtro inserito." : "Nessun dibattito disponibile."}
                className="ml-4 mr-4"
                first={first}
                onPage={(e) => {
                    setFirst(e.first);
                    setRows(e.rows);
                }}
                pt={{
                    paginator: { root: { className: 'border-none pb-0' } },
                }}
            />
        </div>
    );
});

export default RisultatiRicerca;
